<template>
  <div class="legal-document-container max-w-4xl mx-auto pb-6 px-6 bg-white shadow-lg rounded-xl">
    
    <div class="top-0 z-20 bg-white pt-6 pb-4 border-b border-gray-100">
      <div class="flex justify-center">
        <div class="inline-flex p-1 bg-gray-100 rounded-lg">
          <button 
            @click="activeLang = 'de'"
            :class="['px-6 py-2 text-sm font-bold rounded-md transition-all', activeLang === 'de' ? 'bg-white shadow text-blue-600' : 'text-gray-500 hover:text-gray-700']"
          >
            Deutsch
          </button>
          <button 
            @click="activeLang = 'en'"
            :class="['px-6 py-2 text-sm font-bold rounded-md transition-all', activeLang === 'en' ? 'bg-white shadow text-blue-600' : 'text-gray-500 hover:text-gray-700']"
          >
            English
          </button>
        </div>
      </div>
    </div>

    <section v-for="group in versionGroups" :key="group.date" class="mb-8">
      <button
        type="button"
        class="w-full text-left sticky top-[73px] z-10 bg-white py-4 border-b-2 border-gray-100 mb-4"
        @click="toggleVersion(group.date)"
        :aria-expanded="isVersionOpen(group.date)"
        :aria-controls="`version-${String(group.date)}`"
      >
        <div class="flex items-center justify-between gap-4">
          <div class="flex items-center">
            <span class="ml-4 px-2 py-1 bg-blue-50 text-blue-600 text-[10px] rounded uppercase font-bold tracking-widest">
              Version
            </span>
            <h2 class="text-xl font-bold text-blue-900">{{ formatDate(group.date) }}</h2>
          </div>
          <span
            class="mr-4 text-blue-900 transition-transform duration-200"
            :class="isVersionOpen(group.date) ? 'rotate-180' : 'rotate-0'"
            aria-hidden="true"
          >
            ▼
          </span>
        </div>
      </button>

      <transition name="accordion">
        <div v-show="isVersionOpen(group.date)" :id="`version-${String(group.date)}`" class="space-y-4">
          <div v-for="entry in group.entries" :key="entryKey(entry)" class="document-section border border-gray-100 rounded-xl overflow-hidden">
            <button
              type="button"
              class="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between gap-4"
              @click="toggleEntry(entryKey(entry))"
              :aria-expanded="isEntryOpen(entryKey(entry))"
              :aria-controls="`entry-${String(entryKey(entry))}`"
            >
              <span class="text-sm font-bold text-gray-700 uppercase tracking-wide">
                {{ entry.document_level_description }} {{ entry.document_level }}
              </span>
              <span
                class="text-gray-500 transition-transform duration-200"
                :class="isEntryOpen(entryKey(entry)) ? 'rotate-180' : 'rotate-0'"
                aria-hidden="true"
              >
                ▼
              </span>
            </button>

            <transition name="accordion">
              <div v-show="isEntryOpen(entryKey(entry))" :id="`entry-${String(entryKey(entry))}`" class="px-4 py-4 bg-white">
                <div v-if="activeLang === 'de'" class="animate-fadeIn">
                  <div
                    class="prose prose-blue max-w-none text-gray-800"
                    v-html="renderMarkdown(entry.text_de_markdown || entry.text_de)"
                  ></div>
                </div>

                <div v-if="activeLang === 'en'" class="animate-fadeIn">
                  <div v-if="entry.text_en || entry.text_en_markdown">
                    <div
                      class="prose prose-blue max-w-none text-gray-800"
                      v-html="renderMarkdown(entry.text_en_markdown || entry.text_en)"
                    ></div>
                  </div>
                  <div v-else class="flex items-center justify-center p-8 bg-gray-50 border border-dashed rounded-lg text-gray-400 italic text-sm">
                    Keine englische Übersetzung vorhanden / No English translation available.
                  </div>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </transition>
    </section>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { marked } from 'marked';

marked.setOptions({
  breaks: true,
  gfm: true
});

const props = defineProps({
  standardTexts: {
    type: Array,
    required: true
  }
});

const activeLang = ref('de');

const openVersions = ref({});
const openEntries = ref({});

const isVersionOpen = (date) => !!openVersions.value[String(date)];
const toggleVersion = (date) => {
  const key = String(date);
  openVersions.value = { ...openVersions.value, [key]: !openVersions.value[key] };
};

const isEntryOpen = (entryId) => !!openEntries.value[String(entryId)];
const toggleEntry = (entryId) => {
  const key = String(entryId);
  openEntries.value = { ...openEntries.value, [key]: !openEntries.value[key] };
};

const entryKey = (entry) => entry?.id ?? entry?.id_norm

// Group + sort by version date (descending). "Unknown" ends up last.
const versionGroups = computed(() => {
  const list = Array.isArray(props.standardTexts) ? props.standardTexts : [];
  const grouped = list.reduce((acc, obj) => {
    const key = obj?.version_date || 'Unknown';
    if (!acc[key]) acc[key] = [];
    acc[key].push(obj);
    return acc;
  }, {});

  const parseKey = (key) => (key === 'Unknown' ? Number.NEGATIVE_INFINITY : new Date(key).getTime());

  return Object.entries(grouped)
    .sort(([a], [b]) => parseKey(b) - parseKey(a))
    .map(([date, entries]) => ({ date, entries }));
});

const renderMarkdown = (text) => {
  if (!text) return '';
  return marked.parse(text);
};

const formatDate = (dateStr) => {
  if (dateStr === 'Unknown') return dateStr;
  return new Date(dateStr).toLocaleDateString('de-DE', {
    day: '2-digit', month: 'long', year: 'numeric'
  });
};
</script>

<style scoped>
.animate-fadeIn {
  animation: fadeIn 0.3s ease-in-out;
}

.accordion-enter-active,
.accordion-leave-active {
  transition: max-height 200ms ease, opacity 200ms ease;
  overflow: hidden;
}
.accordion-enter-from,
.accordion-leave-to {
  max-height: 0;
  opacity: 0;
}
.accordion-enter-to,
.accordion-leave-from {
  max-height: 2000px;
  opacity: 1;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(5px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Typography styles for the rendered Markdown */
:deep(.prose h2) {
  @apply text-lg font-extrabold text-gray-900 border-b border-gray-100 pb-1 mb-4;
}
:deep(.prose p) {
  @apply leading-relaxed mb-4;
}
</style>